import React, { useState, useEffect } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, Briefcase, FolderKanban, FileText, ScanLine,
  CalendarClock, FileCheck2, Stethoscope, FileSignature, Plane, UserCheck, Rocket,
  IdCard, Wallet, Receipt, UserCog, ListTodo, CalendarCheck, CalendarOff, Bell,
  BarChart3, Workflow, Globe, ShieldCheck, ScrollText, Settings, LogOut, Menu, X,
  FileSpreadsheet, HeartHandshake, Banknote, TrendingDown,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import ResourcePage from "./ResourcePage";
import AdminDashboard from "./AdminDashboard";
import DocumentsAdmin from "./DocumentsAdmin";
import ApplicationsAdmin from "./ApplicationsAdmin";
import ImportCandidates from "./ImportCandidates";

// roles: which roles can see item. super_admin+admin see all.
const R_ALL = ["admin", "recruitment_staff", "hr", "finance"];
const R_REC = ["admin", "recruitment_staff"];
const R_HR = ["admin", "hr"];
const R_FIN = ["admin", "finance"];

const NAV = [
  { group: "Overview", items: [
    { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, roles: R_ALL },
  ]},
  { group: "Recruitment", items: [
    { to: "/admin/candidates", label: "Candidates", icon: Users, roles: [...R_REC, "hr"] },
    { to: "/admin/agencies", label: "Agencies", icon: HeartHandshake, roles: R_ALL },
    { to: "/admin/clients", label: "Employers / Clients", icon: Building2, roles: R_ALL },
    { to: "/admin/jobs", label: "Jobs", icon: Briefcase, roles: R_ALL },
    { to: "/admin/projects", label: "Projects", icon: FolderKanban, roles: R_ALL },
    { to: "/admin/applications", label: "Applications", icon: Workflow, roles: R_REC },
    { to: "/admin/documents", label: "Documents", icon: ScanLine, roles: R_REC },
    { to: "/admin/enquiries", label: "Enquiries", icon: FileText, roles: R_REC },
  ]},
  { group: "Pipeline", items: [
    { to: "/admin/interviews", label: "Interviews", icon: CalendarClock, roles: R_REC },
    { to: "/admin/offers", label: "Offers", icon: FileCheck2, roles: R_REC },
    { to: "/admin/medical", label: "Medical", icon: Stethoscope, roles: R_REC },
    { to: "/admin/contracts", label: "MOL / Contracts", icon: FileSignature, roles: R_REC },
    { to: "/admin/visa", label: "Visa / Processing", icon: Plane, roles: R_REC },
    { to: "/admin/joining", label: "Joining / Ticket", icon: UserCheck, roles: R_REC },
    { to: "/admin/deployments", label: "Deployment", icon: Rocket, roles: R_REC },
    { to: "/admin/employees", label: "Employees", icon: IdCard, roles: [...R_HR, "recruitment_staff"] },
  ]},
  { group: "Finance", items: [
    { to: "/admin/commissions", label: "Commissions", icon: Wallet, roles: [...R_FIN, "recruitment_staff"] },
    { to: "/admin/invoices", label: "Invoices", icon: Receipt, roles: R_FIN },
    { to: "/admin/payments", label: "Payments", icon: Banknote, roles: R_FIN },
    { to: "/admin/expenses", label: "Expenses", icon: TrendingDown, roles: R_FIN },
  ]},
  { group: "HR & Staff", items: [
    { to: "/admin/staff", label: "Staff", icon: UserCog, roles: R_HR },
    { to: "/admin/tasks", label: "Tasks", icon: ListTodo, roles: R_ALL },
    { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck, roles: R_HR },
    { to: "/admin/leave", label: "Leave", icon: CalendarOff, roles: R_HR },
  ]},
  { group: "System", items: [
    { to: "/admin/notifications", label: "Notifications", icon: Bell, roles: R_ALL },
    { to: "/admin/import", label: "Excel Import", icon: FileSpreadsheet, roles: R_REC },
    { to: "/admin/workflows", label: "SOP / Workflow", icon: Workflow, roles: ["admin"] },
    { to: "/admin/countries", label: "Countries", icon: Globe, roles: R_ALL },
    { to: "/admin/users", label: "Users", icon: ShieldCheck, roles: ["admin"] },
    { to: "/admin/audit_logs", label: "Audit Logs", icon: ScrollText, roles: ["admin"] },
  ]},
];

function Sidebar({ onNav }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const can = (roles) => user?.role === "super_admin" || roles.includes(user?.role);
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="h-full flex flex-col brand-gradient-navy">
      <div className="p-5 border-b border-white/10"><Logo variant="light" showTagline /></div>
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
        {NAV.map((grp) => {
          const items = grp.items.filter((i) => can(i.roles));
          if (items.length === 0) return null;
          return (
            <div key={grp.group}>
              <div className="px-3 text-[10px] uppercase tracking-wider text-sky-300/60 mb-1.5">{grp.group}</div>
              <div className="space-y-0.5">
                {items.map((i) => (
                  <NavLink key={i.to} to={i.to} end={i.end} onClick={onNav} data-testid={`admin-nav-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-[#0088FF] text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
                    <i.icon className="h-[18px] w-[18px]" /> {i.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-1 text-xs text-slate-400 truncate">{user?.name} · {user?.role}</div>
        <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-rose-300 hover:bg-white/10"><LogOut className="h-4 w-4" /> Logout</button>
      </div>
    </div>
  );
}

export default function AdminApp() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  useEffect(() => { setMobileOpen(false); }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:block w-64 fixed inset-y-0 z-30"><Sidebar /></aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="w-72"><Sidebar onNav={() => setMobileOpen(false)} /></div>
          <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 h-16 px-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="admin-mobile-toggle">{mobileOpen ? <X /> : <Menu />}</button>
            <span className="font-display font-bold text-[#0A192F] flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#0088FF]" /> Emira ERP</span>
          </div>
          <div className="text-sm text-slate-500">{user?.email}</div>
        </header>
        <main className="flex-1 p-5 lg:p-7">
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="applications" element={<ApplicationsAdmin />} />
            <Route path="documents" element={<DocumentsAdmin />} />
            <Route path="import" element={<ImportCandidates />} />
            <Route path=":resource" element={<ResourcePage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
